import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { IDataTableRef } from 'src/components/DataTable/shared/types';
import { formatPrice } from 'src/helpers/price';
import FlashProductsActionCell from '../../columns/FlashProductsActionCell';
import FlashProductsPeriodCell from '../../columns/FlashProductsPeriodCell';
import FlashProductsProductInfoCell from '../../columns/FlashProductsProductInfoCell';
import useFlashDiscountPeriodTypes from '../../services/hooks/use-flash-discount-period-types';
import type { FlashDiscountProductListItem } from '../../services/models/flash-discount';

export default function useFlashProductsColumns(dataTableRef: React.RefObject<IDataTableRef>) {
  const flashDiscountPeriodTypes = useFlashDiscountPeriodTypes();

  const columns = useMemo(() => {
    if (!flashDiscountPeriodTypes.isSuccess) return [];

    const ColumnHelper = createColumnHelper<FlashDiscountProductListItem>();

    return [
      /** Column => Flaş Ürün Bilgileri */
      ColumnHelper.display({
        id: 'info',
        header: 'Flaş Ürün Bilgileri',
        cell: (info) => {
          return <FlashProductsProductInfoCell cellContext={info as any} />;
        },
        meta: {
          columnClassName: 'min-w-350px product-name bg-light border-end ps-4',
          cellClassName: 'ps-4 product-name border-end',
        },
      }),

      /** Column => Güncel Satış Fiyatı */
      ColumnHelper.display({
        id: 'price',
        header: 'Güncel Satış Fiyatı / Komisyon',
        cell: (info) => {
          const item = info?.row?.original;
          const price = item?.price;
          const commission = item?.commission;

          return (
            <div className="d-flex flex-column gap-4">
              <div className="d-flex gap-2 align-items-center">
                <b>Güncel Satış Fiyatı:</b>
                <span>{formatPrice(price, { suffix: ' TL' })}</span>
              </div>

              <div className="d-flex gap-2 align-items-center">
                <b>Komisyon:</b>
                <span>{commission?.percentage ? `%${commission?.percentage}` : '-'}</span>
              </div>
            </div>
          );
        },
      }),

      /** Column => Flaş Ürün Periyotları */
      ...flashDiscountPeriodTypes.data?.map((period) =>
        ColumnHelper.display({
          id: `period-${period.id}`,
          header: period.title,
          cell: (info) => {
            return <FlashProductsPeriodCell showSimulatedDiscountPrice cellContext={info as any} period={period as any} />;
          },
        }),
      ),

      /** Column => İşlemler */
      ColumnHelper.display({
        id: 'actions',
        header: 'İşlemler',
        cell: (info) => {
          return <FlashProductsActionCell dataTableRef={dataTableRef} cellContext={info as any} />;
        },
      }),
    ];
  }, [flashDiscountPeriodTypes, dataTableRef]);

  return columns;
}
